import { toaster } from '@saleshandy/design-system';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
// import Modal from '../design-system/components/atoms/modal';

import Button from '../design-system/ui/button';
import { Cross } from '../svg';
import { accessibleOnClick } from '../utils/accessible-on-click';
import {
  convertHtmlToMergeTagFormat,
  convertSpinsToChips,
  stripChipTagsFromHtml,
  stripErrorTagHtml,
  stripMergeTagHtml,
  stripVariableTagHtml,
} from './helpers';
import { MergeTagOption } from '../components/overlay-merge-tags';
import SingleEditorInput from '../../components/sequence/shared/modals/email-modal/components/single-editor-input';
import { convertHtmlToText } from '../../components/sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';
import Modal from '../design-system/ui/modal/modal';

// Helper function to clean HTML content from TinyMCE
const cleanSpinHtml = (html: string): string => {
  if (!html) return html;

  return html
    .trim() // Remove leading/trailing whitespace
    .replace(/\n/g, '') // Remove newline characters
    .replace(/<div>\s*<\/div>$/g, '') // Remove trailing empty divs
    .replace(/<div>\s*<\/div>/g, '') // Remove all empty divs
    .trim();
};

const SpintaxTagEditorModal = ({
  show,
  onClose,
  onHide,
  hideHeader,
  hideFooter,
  modalClassName,
  spintaxTagId,
  onContentChange,
  mergeTagOptions,
  tagsOptionsForInlineDropdown,
  ...modalProps
}: {
  show: boolean;
  onClose: () => void;
  onHide: () => void;
  hideHeader: boolean;
  hideFooter: boolean;
  modalClassName: string;
  spintaxTagId: string;
  mergeTagOptions: MergeTagOption[];
  tagsOptionsForInlineDropdown: any;
  onContentChange: () => void;
  [key: string]: any;
}) => {
  const spintaxTagRef = useRef<HTMLDivElement>(null);

  const [spinsList, setSpinsList] = useState<
    { spinId: string; spin: string }[]
  >([]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleSaveValue = useCallback(() => {
    // Filter out empty spin values (convert HTML to plain text to check)
    const nonEmptySpins = spinsList?.filter((s) => {
      const plainText = convertHtmlToText(
        stripChipTagsFromHtml(s?.spin),
        false,
      );
      return plainText?.trim() !== '';
    });

    const plainValues = nonEmptySpins?.map((s) => {
      const plainText =
        convertHtmlToText(stripChipTagsFromHtml(s?.spin), false) || '';
      return {
        spinId: s.spinId,
        plainText: plainText.trim(),
      };
    });

    // Check for duplicates in the current state
    const seenValues = new Set<string>();
    const hasDuplicate = plainValues?.some((item) => {
      const lowerValue = item?.plainText?.toLowerCase();
      if (lowerValue && seenValues?.has(lowerValue)) {
        return true;
      }
      if (lowerValue) {
        seenValues.add(lowerValue);
      }
      return false;
    });

    // Check for validation errors (like {spin} or {endspin})
    const hasValidationError = plainValues?.some(
      (item) =>
        item.plainText?.includes('{spin}') ||
        item.plainText?.includes('{endspin}'),
    );

    if (hasValidationError || hasDuplicate) {
      toaster.error('Fix all errors before inserting spintax tags');
      return;
    }

    if (spintaxTagRef?.current) {
      const element = spintaxTagRef.current;

      const spinChips = element?.querySelector('.spin-chips');
      if (spinChips) {
        // Clean HTML before converting to chips to remove unnecessary \n and empty divs
        const cleanedSpins =
          nonEmptySpins?.map((s) => cleanSpinHtml(s?.spin)) || [];
        spinChips.innerHTML = convertSpinsToChips(cleanedSpins)?.join('') || '';
      }

      // Force rerender after value changes
      setTimeout(() => {
        onContentChange();
      }, 100);
    }
    onClose();
  }, [spinsList, onClose, onContentChange]);

  const validateSpin = useCallback((value: string): string => {
        const regex = /<|>/;
    const hasError = regex.test(value || '');
    if (value.includes('{spin}') || value?.includes('{endspin}')) {
      return '{spin} and {endspin} are not allowed in spintax tags';
    }
    if(hasError){
      return 'special characters like < and > aren’t allowed.';
    }
    return '';
  }, []);

  const handleSpinChange = useCallback(
    (spinId: string) => (content: string) => {
      // Normalize the editor HTML to plain text for duplicate checking
      const plainValue =
        convertHtmlToText(stripChipTagsFromHtml(content), false) || '';

      const error = validateSpin(plainValue);
      setSpinsList((prev) => {
        const isDuplicate = prev?.some((s) => {
          if (s?.spinId === spinId) return false;
          const otherPlainValue =
            convertHtmlToText(stripChipTagsFromHtml(s?.spin), false) || '';
          return (
            otherPlainValue?.trim()?.toLowerCase() ===
            plainValue?.trim()?.toLowerCase()
          );
        });

        setErrors((prevErrors) => ({
          ...prevErrors,
          [spinId]: isDuplicate
            ? 'Duplicate Spin Found. Please try another'
            : error || '',
        }));

        // Store the HTML content (not plain text)
        return prev?.map((s) =>
          s?.spinId === spinId ? { ...s, spin: content } : s,
        );
      });
    },
    [],
  );

  useEffect(() => {
    // Only run when the modal opens (show becomes true) or spintaxTagId changes
    if (!show || !spintaxTagId) return;

    const ele = document.getElementById(spintaxTagId);

    spintaxTagRef.current = ele as HTMLDivElement;
    const element = spintaxTagRef.current;

    if (element) {
      const spins = element.querySelectorAll('.spin-tag-chip');
      setSpinsList(
        Array.from(spins)?.map((spin) => {
          const formattedSpin = convertHtmlToMergeTagFormat(
              stripChipTagsFromHtml(spin?.innerHTML || ''),
            tagsOptionsForInlineDropdown,
          );

          return {
            spinId: uuidv4(),
            spin: formattedSpin,
          };
        }) || [],
      );
    }
  }, [show, spintaxTagId]);

  const handleAddSpinTag = useCallback(() => {
    setSpinsList((prev) => [...prev, { spinId: uuidv4(), spin: '' }]);
  }, []);

  const handleRemoveSpinTag = useCallback(
    (spinIdToRemove: string) => {
      if (spinsList?.length <= 2) {
        toaster.error('At least 2 spintax tags are required to insert ');
        return;
      }
      setSpinsList((prev) => prev?.filter((s) => s?.spinId !== spinIdToRemove));
      // Remove error for this spin tag
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[spinIdToRemove];
        return newErrors;
      });
    },
    [spinsList],
  );

  return (
    <Modal
      title="{|} Add Spintax"
      show={show}
      onHide={onClose}
      hideHeader={hideHeader}
      hideFooter={hideFooter}
      className={`spintax-tag-modal ${modalClassName}`}
      {...modalProps}
    >
      <div className="spintax-tag-modal-container">
        <div className="spintax-tag-modal-container__body">
          {spinsList?.map((spin) => (
            <div
              key={spin.spinId}
              className={`spintax-tag-modal-container__body-spin-tag ${
                errors[spin?.spinId] ? 'spintax-error' : ''
              }`}
            >
              <div className="spintax-tag-modal-input-wrapper">
                <SingleEditorInput
                  inputValue={spin?.spin}
                  placeholder="Add New Spin"
                  isGeneratingWithAi={false}
                  disableEditMode={false}
                  onInputChange={handleSpinChange(spin.spinId)}
                  enableEmoji={false}
                  enableSpintax={false}
                  showTitle=""
                  mergeTagOptions={mergeTagOptions}
                />

                <span
                  {...accessibleOnClick(() => {
                    if (spinsList?.length > 2) {
                      handleRemoveSpinTag(spin?.spinId);
                    }
                  })}
                  className="spintax-tag-modal-container__body-spin-tag-remove"
                >
                  <Cross
                    width={16}
                    height={16}
                    className={`spintax-tag-modal-container__body-spin-tag-remove-icon ${
                      spinsList?.length <= 2 ? 'disable-cross-icon' : ''
                    }`}
                  />
                </span>
              </div>
              {errors[spin?.spinId] && (
                <span className="spin-tag-error-message">
                  {errors[spin?.spinId]}
                </span>
              )}
            </div>
          ))}

          <div className="spintax-tag-modal-container__add-spintax">
            <Button className="add-spintax-button" onClick={handleAddSpinTag}>
              + Add Spin
            </Button>
          </div>
        </div>

        <div className="spintax-tag-modal-container__footer">
          <Button
            className="spintax-tag-modal-container__footer-cancel-button"
            {...accessibleOnClick(onClose)}
          >
            Cancel
          </Button>
          <Button
            disabled={
              spinsList?.length < 2 ||
              Object.values(errors).some((error) => error !== '')
            }
            className="spintax-tag-modal-container__footer-submit-button"
            {...accessibleOnClick(handleSaveValue)}
          >
            Insert
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(SpintaxTagEditorModal);
